import { type Calendar, getCalendars } from "@/components/calendars/api.ts";
import { CalendarView } from "@/components/calendars/CalendarView.tsx";
import { useEventDrag } from "@/components/calendars/useEventDrag.tsx";
import { useQuery } from "@tanstack/react-query";
import { type ComponentProps, useMemo } from "react";

type CalendarViewProps = Omit<ComponentProps<typeof CalendarView>, "eventDragFcProps">;

/**
 * `CalendarView` with drag-to-reschedule wired in. Splits the wiring out of
 * `CalendarView` itself so callers that don't want the drag behavior
 * (`CalendarPreview`, `CalendarAvailabilityPicker`) don't pay the
 * `useRightSidebar` / `useMutation` / dialog-render cost — and don't need
 * a `RightSidebar` context in their ancestor tree.
 *
 * The calendars query is shared with `CalendarView`'s own copy (React Query
 * dedupes by key), so this wrapper doesn't cause a second network round trip.
 */
export const EditableCalendarView = (props: CalendarViewProps) => {
  const { data: calendars } = useQuery<Calendar[]>({
    queryKey: ["calendars"],
    queryFn: getCalendars,
  });

  // `undefined` when calendars haven't loaded yet — `canDragEvent` treats
  // that as "not draggable," so events silently fall through to read-only
  // for the first tick rather than letting the user start a drag that the
  // predicate would then reject mid-flight.
  // Keyed by the string `calendarId` (Google's calendar identifier — what
  // `event.calendarId` carries), NOT the numeric PK `id`. canDragEvent looks
  // up by `event.calendarId`.
  const calendarsById = useMemo(() => {
    if (!calendars) return undefined;
    return new Map(calendars.map((c) => [c.calendarId, c]));
  }, [calendars]);

  const { fcProps, dragElements } = useEventDrag({
    enabled: true,
    calendarsById,
  });

  return (
    <>
      <CalendarView {...props} eventDragFcProps={fcProps} />
      {dragElements}
    </>
  );
};
